import React, { useRef, useState, useEffect } from "react";
import Table from "../../../../core/common/dataTable/index";
import PredefinedDateRanges from "../../../../core/common/datePicker";
import CommonSelect from "../../../../core/common/commonSelect";
import {
  statusList,
  CLASSES_SORT_OPTIONS,
} from "../../../../core/common/selectoption/selectoption";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import TooltipOption from "../../../../core/common/tooltipOption";
import {
  submitLeave,
  getAllLeaves,
  editLeave,
  removeLeave,
  approveOrRejectLeave,
  type Leave,
  type LeaveCreateData,
  type LeaveUpdateData,
  type LeaveQuotaSummary,
  getAllLeaveQuotas,
  cancelLeave,
  getLeaveQuotaSummary,
} from "../../../../service/leaves";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { useReactToPrint } from "react-to-print";
import moment from "moment";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../core/data/redux/slice/user";
import { selectCurrentSession } from "../../../../core/data/redux/slice/sessionsSlice";
import { selectCurrentBranch } from "../../../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../../../utils/campusLogos";
import { isStudent } from "../../../../utils/rbacHelper";
import StaffDropdown from "../../../../core/common/StaffDropdown";
import { DEFAULT_BRANCH_ID } from "../../../../constant";
import StudentLeavePanel, { type StudentLeavePanelHandle } from "../../student-leave/StudentLeavePanel";

const LEAVE_SORT_OPTIONS = CLASSES_SORT_OPTIONS as {
  label: string;
  value: { sortBy: string; sortOrder: "asc" | "desc" };
}[];

const ListLeaves = () => {
  const routes = all_routes;
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const componentRef = useRef<HTMLDivElement>(null);
  const user = useSelector(selectUser);
  const currentSession = useSelector(selectCurrentSession);
  const currentBranch = useSelector(selectCurrentBranch);
  const isUserStudent = isStudent(user);

  // Students never see this toggle — they only ever have their own leave
  // applications, which is the Employees-shaped view already (the tab exists
  // for staff managing leave on someone else's behalf). Student leave uses a
  // different, simpler mechanic (no approval step) than the request/approve
  // workflow below, so it's a separate panel rather than a filter on it.
  const [activeTab, setActiveTab] = useState<"employees" | "students">("employees");
  const studentPanelRef = useRef<StudentLeavePanelHandle>(null);

  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    total: 0,
    limit: 10,
  });

  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    status: "",
    type: "",
    startDate: "",
    endDate: "",
    userId: isUserStudent ? user._id : "",
  });

  const [formData, setFormData] = useState<LeaveCreateData>({
    userId: isUserStudent ? user._id : "",
    startDate: "",
    endDate: "",
    type: "",
    reason: "",
    status: "",
    totalDays: 0,
  });

  const [editFormData, setEditFormData] = useState<
    LeaveUpdateData & { id: string; totalDays: number }
  >({
    id: "",
    userId: "",
    startDate: "",
    endDate: "",
    type: "",
    reason: "",
    status: "",
    totalDays: 0,
  });

  const [selectedStaffForAdd, setSelectedStaffForAdd] = useState<any>(null);
  const [selectedStaffForEdit, setSelectedStaffForEdit] = useState<any>(null);

  const [leaveQuotaSummary, setLeaveQuotaSummary] =
    useState<LeaveQuotaSummary | null>(null);
  const [quotaSummaryLoading, setQuotaSummaryLoading] = useState(false);
  const [quotaSummaryError, setQuotaSummaryError] = useState(false);

  const [sortBy, setSortBy] = useState(LEAVE_SORT_OPTIONS[0]);
  const [searchText, setSearchText] = useState("");
  const [leaveTypeOptions, setLeaveTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const formatLeaveType = (value: string) =>
    value
      .replace(/[-_]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const fetchLeaveTypeOptions = async () => {
    try {
      const response = await getAllLeaveQuotas({
        ...(currentSession?._id && { session: currentSession?._id }),
      });
      const rawList = Array.isArray(response?.data) ? response.data : [];
      const options = rawList
        .map((item: any) => {
          if (!item?._id || !item?.type) return null;
          return {
            value: String(item._id),
            label: formatLeaveType(String(item.type)),
          };
        })
        .filter(Boolean) as { value: string; label: string }[];

      setLeaveTypeOptions(options);
    } catch (error) {
      console.error("Error fetching leave types from quota API:", error);
      setLeaveTypeOptions([]);
    }
  };

  const resetAddLeaveForm = () => {
    setFormData({
      userId: isUserStudent ? user._id : "",
      startDate: "",
      endDate: "",
      type: "",
      reason: "",
      status: "",
      totalDays: 0,
    });
    setSelectedStaffForAdd(null);
    setLeaveQuotaSummary(null);
    setQuotaSummaryError(false);
    setQuotaSummaryLoading(false);
  };

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const params: any = {
        ...filters,
        sortBy: sortBy.value.sortBy,
        sortOrder: sortBy.value.sortOrder,
        searchText,
      };

      if (isUserStudent && !params.userId) {
        params.userId = user._id;
      }

      const response = await getAllLeaves(params);
      if (response.success) {
        setLeaves(response.data.leaves);
        setPagination(response.data.pagination);
      } else {
        toast.error("Failed to fetch leaves");
      }
    } catch (error: any) {
      toast.error(error.message || "Error fetching leaves");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, [filters, sortBy, searchText]);

  useEffect(() => {
    fetchLeaveTypeOptions();
  }, [currentSession?._id]);

  // Fetch leave quota summary when employee and leave type are selected (Add modal)
  useEffect(() => {
    const userId = formData.userId;
    const type = formData.type;
    if (isUserStudent || !userId || !type) {
      setLeaveQuotaSummary(null);
      setQuotaSummaryError(false);
      return;
    }
    let cancelled = false;
    setQuotaSummaryLoading(true);
    setLeaveQuotaSummary(null);
    setQuotaSummaryError(false);
    getLeaveQuotaSummary({
      userId,
      type,
      ...(currentSession?._id && { session: currentSession?._id }),
      ...(formData.startDate && { startDate: formData.startDate }),
    })
      .then((res: any) => {
        if (cancelled) return;
        const raw = res?.data ?? res;
        const totalLeaves = raw?.totalLeaves ?? raw?.total;
        const takenLeaves = raw?.takenLeaves ?? raw?.taken;
        const balanceLeaves = raw?.balanceLeaves ?? raw?.balance ?? raw?.remaining;
        if (
          typeof totalLeaves === "number" ||
          typeof takenLeaves === "number" ||
          typeof balanceLeaves === "number"
        ) {
          setLeaveQuotaSummary({
            totalLeaves: typeof totalLeaves === "number" ? totalLeaves : 0,
            takenLeaves: typeof takenLeaves === "number" ? takenLeaves : 0,
            balanceLeaves: typeof balanceLeaves === "number" ? balanceLeaves : 0,
          });
          setQuotaSummaryError(false);
        } else {
          setLeaveQuotaSummary(null);
          setQuotaSummaryError(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLeaveQuotaSummary(null);
          setQuotaSummaryError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setQuotaSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formData.userId,
    formData.type,
    formData.startDate,
    isUserStudent,
    currentSession?._id,
  ]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    const updatedFormData = {
      ...formData,
      [name]: value,
    };

    if (name === "startDate" || name === "endDate") {
      const totalDays = calculateTotalDays(
        name === "startDate" ? value : formData.startDate,
        name === "endDate" ? value : formData.endDate
      );
      updatedFormData.totalDays = totalDays;
    }

    setFormData(updatedFormData);
  };

  const handleEditInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    const updatedEditFormData = {
      ...editFormData,
      [name]: value,
    };

    if (name === "startDate" || name === "endDate") {
      const totalDays = calculateTotalDays(
        name === "startDate" ? value : editFormData.startDate,
        name === "endDate" ? value : editFormData.endDate
      );
      updatedEditFormData.totalDays = totalDays;
    }

    setEditFormData(updatedEditFormData);
  };

  const handleSubmitLeave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: LeaveCreateData = {
        ...formData,
        type: formData.type,
        userId: isUserStudent ? user._id : formData.userId,
        ...(currentSession?._id && { session: currentSession?._id }),
        branch: DEFAULT_BRANCH_ID,
      };

      const response = await submitLeave(payload);
      if (response.success) {
        if (response.quotaSummary) {
          setLeaveQuotaSummary(response.quotaSummary);
          setQuotaSummaryError(false);
        }
        toast.success("Leave submitted successfully");
        resetAddLeaveForm();
        const el = document.getElementById("add_leaves");
        if (el) {
          el.setAttribute("data-bs-dismiss", "modal");
          el.click();
          el.removeAttribute("data-bs-dismiss");
        }
        fetchLeaves();
      } else {
        toast.error(response.message || "Failed to submit leave");
      }
    } catch (error: any) {
      toast.error(error.message || "Error submitting leave");
    }
  };

  const handleEditLeave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editFormData.status !== "pending") {
      toast.error(
        `Cannot edit ${editFormData.status} leave. Only pending leaves can be edited.`
      );

      const el = document.getElementById("edit_leaves");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
      return;
    }

    try {
      const { id, ...updateData } = editFormData;

      if (
        !updateData.totalDays &&
        editFormData.startDate &&
        editFormData.endDate
      ) {
        updateData.totalDays = calculateTotalDays(
          editFormData.startDate,
          editFormData.endDate
        );
      }

      const response = await editLeave(id, updateData);

      if (response.success) {
        toast.success("Leave updated successfully");
        const el = document.getElementById("edit_leaves");
        if (el) {
          el.setAttribute("data-bs-dismiss", "modal");
          el.click();
          el.removeAttribute("data-bs-dismiss");
        }
        fetchLeaves();
      } else {
        toast.error(response.message || "Failed to update leave");
      }
    } catch (error: any) {
      toast.error(error.message || "Error updating leave");
    }
  };

  const handleDeleteLeave = async (id: string) => {
    try {
      const response = await removeLeave(id);
      if (response.success) {
        toast.success("Leave deleted successfully");
        const el = document.getElementById("delete-modal");
        if (el) {
          el.setAttribute("data-bs-dismiss", "modal");
          el.click();
          el.removeAttribute("data-bs-dismiss");
        }
        fetchLeaves();
      } else {
        toast.error(response.message || "Failed to delete leave");
      }
    } catch (error: any) {
      toast.error(error.message || "Error deleting leave");
    }
  };

  const handleCancelLeave = async (id: string) => {
    try {
      console.log("Cancelling leave with ID:", id);

      const response = await cancelLeave(id);
      console.log("Cancel response:", response);

      if (response.success) {
        toast.success("Leave cancelled successfully");
        fetchLeaves();
      } else {
        toast.error(response.message || "Failed to cancel leave");
      }
    } catch (error: any) {
      console.error("Cancel error:", error);
      toast.error(error.message || "Error cancelling leave");
    }
  };

  const handleApproveRejectLeave = async (
    id: string,
    status: "approved" | "rejected"
  ) => {
    try {
      const response = await approveOrRejectLeave(id, { status });
      if (response.success) {
        toast.success(`Leave ${status} successfully`);
        fetchLeaves();
      } else {
        toast.error(response.message || `Failed to ${status} leave`);
      }
    } catch (error: any) {
      toast.error(error.message || `Error ${status} leave`);
    }
  };

  const setupEditForm = (leave: Leave) => {
    const formatDateForInput = (dateString: string) => {
      const date = new Date(dateString);
      return date.toISOString().split("T")[0];
    };

    setEditFormData({
      id: leave._id,
      userId: leave.user?._id,
      startDate: formatDateForInput(leave.startDate),
      endDate: formatDateForInput(leave.endDate),
      type: leave.type,
      status: leave.status,
      reason: leave.reason,
      totalDays:
        leave.totalDays || calculateTotalDays(leave.startDate, leave.endDate),
    });

    setSelectedStaffForEdit(
      leave.user
        ? {
            value: leave.user._id,
            label: `${leave.user.firstName} ${leave.user.lastName}`,
          }
        : null
    );
  };

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
    fetchLeaves();
  };

  const handleResetFilters = () => {
    setFilters({
      page: 1,
      limit: 10,
      status: "",
      type: "",
      startDate: "",
      endDate: "",
      userId: isUserStudent ? user._id : "",
    });
    setSortBy(LEAVE_SORT_OPTIONS[0]);
  };

  const handleSortSelect = (item: {
    label: string;
    value: { sortBy: string; sortOrder: "asc" | "desc" };
  }) => {
    setSortBy(item);
  };

  const handlePrint = useReactToPrint({ contentRef: componentRef });

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      leaves.map((leave) => ({
        User: `${leave.user.firstName} ${leave.user.lastName}`,
        "Leave Type":
          leaveTypeOptions.find((option) => option.value === leave.type)?.label ||
          "N/A",
        "Start Date": new Date(leave.startDate).toLocaleDateString(),
        "End Date": new Date(leave.endDate).toLocaleDateString(),
        "Total Days": leave.totalDays,
        Reason: leave.reason,
        Status: leave.status,
        "Created Date": new Date(leave.createdAt).toLocaleDateString(),
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leaves");
    XLSX.writeFile(wb, "leaves.xlsx");
  };

  const handleRefresh = async () => {
    await fetchLeaves();
  };

  const calculateTotalDays = (
    startDate: string | undefined,
    endDate: string | undefined
  ): number => {
    if (!startDate || !endDate || startDate === "" || endDate === "") return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;

    const timeDiff = end.getTime() - start.getTime();
    if (timeDiff < 0) return 0;

    return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
  };

  const columns = [
    ...(isUserStudent
      ? []
      : [
          {
            title: "User",
            dataIndex: "user",
            render: (user: Leave["user"]) => (
              <span>{`${user.firstName} ${user.lastName}`}</span>
            ),
            sorter: true,
          },
        ]),
    {
      title: "Leave Type",
      dataIndex: "type",
      render: (type: string) =>
        leaveTypeOptions.find((option) => option.value === type)?.label || "N/A",
      sorter: true,
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: true,
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: true,
    },
    {
      title: "Total Days",
      dataIndex: "totalDays",
      render: (totalDays: number, record: Leave) => (
        <span className="badge bg-info">
          {totalDays} {totalDays === 1 ? "Day" : "Days"}
        </span>
      ),
      sorter: true,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      render: (reason: string) => (
        <span title={reason}>
          {reason.length > 50 ? `${reason.substring(0, 50)}...` : reason}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => (
        <>
          {status === "approved" ? (
            <span className="badge badge-soft-success d-inline-flex align-items-center">
              <i className="ti ti-circle-check fs-5 me-1"></i>
              Approved
            </span>
          ) : status === "rejected" ? (
            <span className="badge badge-soft-danger d-inline-flex align-items-center">
              <i className="ti ti-circle-x fs-5 me-1"></i>
              Rejected
            </span>
          ) : status === "cancelled" ? (
            <span className="badge badge-soft-secondary d-inline-flex align-items-center">
              <i className="ti ti-circle-minus fs-5 me-1"></i>
              Cancelled
            </span>
          ) : (
            <span className="badge badge-soft-warning d-inline-flex align-items-center">
              <i className="ti ti-clock fs-5 me-1"></i>
              Pending
            </span>
          )}
        </>
      ),
      sorter: true,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: Leave) => (
        <div className="d-flex gap-2">
          {!isUserStudent && record.status === "pending" && (
            <>
              <button
                className={`btn btn-sm btn-outline-success ${
                  user.permissions?.includes("leaves.status") ||
                  !user.permissions?.length
                    ? ""
                    : "not-allowed"
                }`}
                onClick={(e) => {
                  if (
                    !user.permissions?.includes("leaves.status") &&
                    !!user.permissions?.length
                  ) {
                    e.preventDefault();
                    e.stopPropagation();
                  } else {
                    handleApproveRejectLeave(record._id, "approved");
                  }
                }}
              >
                Approve
              </button>
              <button
                className={`btn btn-sm btn-outline-danger ${
                  user.permissions?.includes("leaves.status") ||
                  !user.permissions?.length
                    ? ""
                    : "not-allowed"
                }`}
                onClick={(e) => {
                  if (
                    !user.permissions?.includes("leaves.status") &&
                    !!user.permissions?.length
                  ) {
                    e.preventDefault();
                    e.stopPropagation();
                  } else {
                    handleApproveRejectLeave(record._id, "rejected");
                  }
                }}
              >
                Reject
              </button>
            </>
          )}
          {(record.status === "pending" || record.status === "approved") && (
            <button
              className={`btn btn-sm btn-outline-warning ${
                (user.permissions?.includes("leaves.cancel") ||
                  !user.permissions?.length) &&
                (record.status === "pending" ||
                  (record.status === "approved" && !isUserStudent))
                  ? ""
                  : "disabled"
              }`}
              onClick={() => handleCancelLeave(record._id)}
              title={
                record.status === "approved" && isUserStudent
                  ? "You cannot cancel approved leaves"
                  : "Cancel this leave"
              }
            >
              <i className="ti ti-circle-minus me-1"></i>
              Cancel
            </button>
          )}
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              {record.status === "pending" && (
                <li>
                  <Link
                    className={`dropdown-item rounded-1 ${
                      user.permissions?.includes("leaves.edit") ||
                      !user.permissions?.length
                        ? ""
                        : "not-allowed"
                    }`}
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#edit_leaves"
                    onClick={(e) => {
                      if (
                        !user.permissions?.includes("leaves.edit") &&
                        !!user.permissions?.length
                      ) {
                        e.preventDefault();
                        e.stopPropagation();
                      } else {
                        setupEditForm(record);
                      }
                    }}
                  >
                    <i className="ti ti-edit-circle me-2" />
                    Edit
                  </Link>
                </li>
              )}
              {(record.status === "pending" ||
                record.status === "approved") && (
                <li>
                  <Link
                    className={`dropdown-item rounded-1 text-warning ${
                      (user.permissions?.includes("leaves.cancel") ||
                        !user.permissions?.length) &&
                      (record.status === "pending" ||
                        (record.status === "approved" && !isUserStudent))
                        ? ""
                        : "not-allowed"
                    }`}
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (
                        (!user.permissions?.includes("leaves.cancel") &&
                          !!user.permissions?.length) ||
                        (record.status === "approved" && isUserStudent)
                      ) {
                        toast.error("You cannot cancel approved leaves");
                      } else {
                        handleCancelLeave(record._id);
                      }
                    }}
                  >
                    <i className="ti ti-circle-minus me-2" />
                    Cancel Leave
                  </Link>
                </li>
              )}

              {(record.status === "cancelled" ||
                record.status === "rejected") && (
                <li>
                  <Link
                    className={`dropdown-item rounded-1 text-danger ${
                      user.permissions?.includes("leaves.delete") ||
                      !user.permissions?.length
                        ? ""
                        : "not-allowed"
                    }`}
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#delete-modal"
                    onClick={(e) => {
                      if (
                        !user.permissions?.includes("leaves.delete") &&
                        !!user.permissions?.length
                      ) {
                        e.preventDefault();
                        e.stopPropagation();
                      } else {
                        setEditFormData((prev) => ({
                          ...prev,
                          id: record._id,
                        }));
                      }
                    }}
                  >
                    <i className="ti ti-trash-x me-2" />
                    Delete
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                {isUserStudent ? "My Leave Applications" : "Leave Management"}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">HRM</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isUserStudent ? "My Leaves" : "Leave Management"}
                  </li>
                </ol>
              </nav>
            </div>
            {activeTab === "employees" ? (
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                <TooltipOption
                  onRefresh={handleRefresh}
                  onExportPdf={handlePrint}
                  onExportExcel={handleExportExcel}
                />
                <div className="mb-2">
                  <Link
                    to="#"
                    className={`btn btn-primary d-flex align-items-center ${
                      user.permissions?.includes("leaves.create") ||
                      !user.permissions?.length
                        ? ""
                        : "not-allowed"
                    }`}
                    data-bs-toggle="modal"
                    data-bs-target="#add_leaves"
                    onClick={(e) => {
                      if (
                        !user.permissions?.includes("leaves.create") &&
                        !!user.permissions?.length
                      ) {
                        e.preventDefault();
                        e.stopPropagation();
                      } else {
                        resetAddLeaveForm();
                        fetchLeaveTypeOptions();
                      }
                    }}
                  >
                    <i className="ti ti-square-rounded-plus me-2" />
                    Apply for Leave
                  </Link>
                </div>
              </div>
            ) : (
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                <TooltipOption
                  onRefresh={async () => studentPanelRef.current?.refresh()}
                  onExportPdf={() => studentPanelRef.current?.print()}
                  onExportExcel={() => studentPanelRef.current?.exportExcel()}
                />
                <div className="mb-2">
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center"
                    data-bs-toggle="modal"
                    data-bs-target="#add_leave_modal"
                  >
                    <i className="ti ti-square-rounded-plus me-2" />
                    Add Leave
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Staff only — a student here is always looking at their own
              applications, never managing anyone else's. */}
          {!isUserStudent && (
            <ul className="nav nav-tabs nav-tabs-solid nav-tabs-rounded-fill mb-3">
              <li className="me-2">
                <button
                  type="button"
                  className={`nav-link rounded fs-13 fw-medium ${activeTab === "employees" ? "active" : ""}`}
                  onClick={() => setActiveTab("employees")}
                >
                  Employees
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className={`nav-link rounded fs-13 fw-medium ${activeTab === "students" ? "active" : ""}`}
                  onClick={() => setActiveTab("students")}
                >
                  Students
                </button>
              </li>
            </ul>
          )}

          {activeTab === "students" ? (
            <StudentLeavePanel type="leave" showOwnToolbar={false} ref={studentPanelRef} />
          ) : (
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">
                {isUserStudent ? "My Leave Applications" : "Leave List"}
              </h4>
              <div className="d-flex align-items-center flex-wrap">
                {!isUserStudent && (
                  <div className="input-icon-start mb-3 me-2 position-relative">
                    <PredefinedDateRanges
                      onDateSelect={({ startDate, endDate }) => {
                        const formattedStartDate =
                          moment(startDate).format("YYYY-MM-DD");
                        const formattedEndDate =
                          moment(endDate).format("YYYY-MM-DD");
                        setFilters((prev) => ({
                          ...prev,
                          startDate: formattedStartDate,
                          endDate: formattedEndDate,
                          page: 1,
                        }));
                      }}
                    />
                  </div>
                )}

                {!isUserStudent && (
                  <div className="dropdown mb-3 me-2">
                    <Link
                      to="#"
                      className="btn btn-outline-light bg-white dropdown-toggle"
                      data-bs-toggle="dropdown"
                      data-bs-auto-close="outside"
                    >
                      <i className="ti ti-filter me-2" />
                      Filter
                    </Link>
                    <div
                      className="dropdown-menu drop-width"
                      ref={dropdownMenuRef}
                    >
                      <form>
                        <div className="d-flex align-items-center border-bottom p-3">
                          <h4>Filter</h4>
                        </div>
                        <div className="p-3 border-bottom">
                          <div className="row">
                            <div className="col-md-12">
                              <div className="mb-3">
                                <label className="form-label">Leave Type</label>
                                <CommonSelect
                                  className="select"
                                  options={leaveTypeOptions}
                                  value={
                                    leaveTypeOptions.find(
                                      (option) => option.value === filters.type
                                    ) || null
                                  }
                                  onChange={(selectedOption) =>
                                    handleFilterChange(
                                      "type",
                                      selectedOption?.value || ""
                                    )
                                  }
                                />
                              </div>
                            </div>
                            <div className="col-md-12">
                              <div className="mb-0">
                                <label className="form-label">Status</label>
                                <CommonSelect
                                  className="select"
                                  options={statusList}
                                  value={
                                    statusList.find(
                                      (option) =>
                                        option.value === filters.status
                                    ) || null
                                  }
                                  onChange={(selectedOption) =>
                                    handleFilterChange(
                                      "status",
                                      selectedOption?.value || ""
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="p-3 d-flex align-items-center justify-content-end">
                          <Link
                            to="#"
                            className="btn btn-light me-3"
                            onClick={handleResetFilters}
                          >
                            Reset
                          </Link>
                          <Link
                            to="#"
                            className="btn btn-primary"
                            onClick={handleApplyClick}
                          >
                            Apply
                          </Link>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {LEAVE_SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${
                            item.label === sortBy.label ? "active" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div
              className="card-body p-0 py-3 printable-content"
              ref={componentRef}
            >
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Leave List</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              <Table
                columns={columns}
                dataSource={leaves}
                pagination={{ ...pagination, onChange: handlePageChange }}
                loading={loading}
                setSearchText={setSearchText}
              />
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Add Leave Modal */}
      <div className="modal fade" id="add_leaves">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Apply for Leave</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                onClick={resetAddLeaveForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleSubmitLeave}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    {!isUserStudent && (
                      <div className="mb-3">
                        <label className="form-label">Employee Name</label>
                        <StaffDropdown
                          isMulti={false}
                          value={selectedStaffForAdd}
                          onChange={(selected) => {
                            setSelectedStaffForAdd(selected);
                            const option = Array.isArray(selected)
                              ? selected[0]
                              : selected;
                            setFormData((prev) => ({
                              ...prev,
                              userId: option?.value || "",
                            }));
                          }}
                          placeholder="Select employee"
                          className="w-100"
                        />
                      </div>
                    )}
                    <div className="mb-3">
                      <label className="form-label">Leave Type</label>
                      <select
                        className="form-control"
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Leave Type</option>
                        {leaveTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {!isUserStudent &&
                      formData.userId &&
                      formData.type &&
                      (quotaSummaryLoading ? (
                        <div className="row g-2 mb-3">
                          <div className="col-4">
                            <label className="form-label small text-muted mb-1">
                              Total Assigned
                            </label>
                            <div className="form-control bg-light">—</div>
                          </div>
                          <div className="col-4">
                            <label className="form-label small text-muted mb-1">
                              Taken
                            </label>
                            <div className="form-control bg-light">—</div>
                          </div>
                          <div className="col-4">
                            <label className="form-label small text-muted mb-1">
                              Remaining
                            </label>
                            <div className="form-control bg-light">—</div>
                          </div>
                          <div className="col-12 small text-muted">
                            Loading leave summary...
                          </div>
                        </div>
                      ) : (
                        <div className="row g-2 mb-3">
                          <div className="col-4">
                            <label className="form-label small text-muted mb-1">
                              Total Assigned
                            </label>
                            <div className="form-control bg-light">
                              {leaveQuotaSummary?.totalLeaves ?? "—"}
                            </div>
                          </div>
                          <div className="col-4">
                            <label className="form-label small text-muted mb-1">
                              Taken
                            </label>
                            <div className="form-control bg-light">
                              {leaveQuotaSummary?.takenLeaves ?? "—"}
                            </div>
                          </div>
                          <div className="col-4">
                            <label className="form-label small text-muted mb-1">
                              Remaining
                            </label>
                            <div className="form-control bg-light">
                              {leaveQuotaSummary?.balanceLeaves ?? "—"}
                            </div>
                          </div>
                          {quotaSummaryError && (
                            <div className="col-12 small text-warning">
                              Summary not available.
                            </div>
                          )}
                        </div>
                      ))}
                    <div className="mb-3">
                      <label className="form-label">Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Total Days</label>
                      <div
                        className={`form-control ${
                          formData.totalDays > 0 ? "bg-light" : ""
                        }`}
                      >
                        {formData.totalDays > 0 ? (
                          <span className="fw-bold">
                            {formData.totalDays}{" "}
                            {formData.totalDays === 1 ? "Day" : "Days"}
                          </span>
                        ) : (
                          <span className="text-muted">
                            Select start and end dates
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Reason</label>
                      <textarea
                        className="form-control"
                        name="reason"
                        value={formData.reason}
                        onChange={handleInputChange}
                        rows={3}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={resetAddLeaveForm}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Apply for Leave
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Leave Modal*/}
      <div className="modal fade" id="edit_leaves">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Leave</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleEditLeave}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    {!isUserStudent && (
                      <div className="mb-3">
                        <label className="form-label">Employee Name</label>
                        <StaffDropdown
                          isMulti={false}
                          value={selectedStaffForEdit}
                          onChange={(selected) => {
                            setSelectedStaffForEdit(selected);
                            const option = Array.isArray(selected)
                              ? selected[0]
                              : selected;
                            setEditFormData((prev) => ({
                              ...prev,
                              userId: option?.value || "",
                            }));
                          }}
                          placeholder="Select employee"
                          className="w-100"
                        />
                      </div>
                    )}
                    <div className="mb-3">
                      <label className="form-label">Leave Type</label>
                      <select
                        className="form-control"
                        name="type"
                        value={editFormData.type}
                        onChange={handleEditInputChange}
                        required
                      >
                        <option value="">Select Leave Type</option>
                        {leaveTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="startDate"
                        value={editFormData.startDate}
                        onChange={handleEditInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="endDate"
                        value={editFormData.endDate}
                        onChange={handleEditInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Total Days</label>
                      <div
                        className={`form-control ${
                          editFormData.totalDays > 0 ? "bg-light" : ""
                        }`}
                      >
                        {editFormData.totalDays > 0 ? (
                          <span className="fw-bold">
                            {editFormData.totalDays}{" "}
                            {editFormData.totalDays === 1 ? "Day" : "Days"}
                          </span>
                        ) : (
                          <span className="text-muted">
                            Select start and end dates
                          </span>
                        )}
                      </div>
                      {editFormData.startDate &&
                        editFormData.endDate &&
                        editFormData.totalDays > 0 && (
                          <div className="mt-1">
                            <small className="text-muted">
                              From{" "}
                              <strong>
                                {new Date(
                                  editFormData.startDate
                                ).toLocaleDateString()}
                              </strong>{" "}
                              to{" "}
                              <strong>
                                {new Date(
                                  editFormData.endDate
                                ).toLocaleDateString()}
                              </strong>
                            </small>
                          </div>
                        )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Reason</label>
                      <textarea
                        className="form-control"
                        name="reason"
                        value={editFormData.reason}
                        onChange={handleEditInputChange}
                        rows={3}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <span className="delete-icon">
                <i className="ti ti-trash-x" />
              </span>
              <h4>Confirm Deletion</h4>
              <p>
                Are you sure you want to delete this leave request? This action
                cannot be undone.
              </p>
              <div className="d-flex justify-content-center">
                <button
                  type="button"
                  className="btn btn-light me-3"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  data-bs-dismiss="modal"
                  onClick={() => handleDeleteLeave(editFormData.id)}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListLeaves;
